import {useEffect, useRef, useState} from "react";
import {useAudioLevel} from "./useAudioLevel";
import {VoiceAssistantScreen} from "./VoiceAssistantScreen";

// Prefixed so these are easy to filter for in devtools while watching a call go through its
// lifecycle. `console.info` for normal lifecycle events, `console.error` for anything that
// stops the call from proceeding — see the "tell what is happening" logging below.
const LOG = "[VoiceCall]";

// One line per raw sip.js status — shown above the orb instead of the generic 5-bucket
// idle/listening/thinking/speaking/error label so what's actually happening (registering,
// dialing, ringing, ...) is visible while it's happening, not just "Connecting…" the whole
// time. Omitted here on purpose: "in-call" (the phase-text/listening/speaking labels below
// already cover that) and "error" (VoiceAssistantScreen's own default + errorMessage already
// covers that, unless overridden by CONNECTION_LOST_TEXT below).
const STATUS_TEXT = {
  idle: "Ready — starting call…",
  connecting: "Connecting to PBX (Asterisk)…",
  registering: "Registering with PBX…",
  registered: "Registered — dialing Voice Assistant…",
  connected: "Connected to PBX…",
  calling: "Calling Voice Assistant…",
  incoming: "Incoming call…",
};

// Short-lived (or until-superseded) messages layered on top of STATUS_TEXT for lifecycle
// moments that aren't a raw sip.js status by themselves — they're edges (a transition into or
// out of a status) rather than a status. Tracked as `phaseText` below.
const ANSWERED_TEXT = "Voice Assistant answered";
const READY_TEXT = "Ready to talk";
const CALL_ENDED_TEXT = "Call ended";
const CONNECTION_LOST_TEXT = "Connection lost";

// Any status past "we've reached the PBX" — used to recognize an unexpected drop into "error"
// as a lost connection rather than e.g. a bad-destination dial failure from a cold start.
const CONNECTED_ISH_STATUSES = new Set(["connecting", "registering", "registered", "connected", "calling", "incoming", "in-call"]);

/**
 * A live SIP call has no explicit "your turn / my turn" signal the way a text-driven voicebot
 * does — both directions are open simultaneously. This lightweight heuristic compares live mic
 * vs. remote-party amplitude (with hysteresis, to avoid flickering on every breath/pause) so
 * the orb can still show a distinct "speaking" look when the far end is clearly the one talking.
 */
function useInCallTurn(inCall, localStream, remoteStream) {
  const [turn, setTurn] = useState("listening");
  const localLevel = useAudioLevel(inCall ? localStream : null);
  const remoteLevel = useAudioLevel(inCall ? remoteStream : null);

  useEffect(() => {
    if (!inCall) return undefined;

    const SPEAKING_ON = 0.16;
    const SPEAKING_OFF = 0.07;
    let speaking = false;
    let raf;

    const tick = () => {
      const remote = remoteLevel.current.level;
      const local = localLevel.current.level;
      if (!speaking && remote > SPEAKING_ON && remote > local * 1.25) {
        speaking = true;
        setTurn("speaking");
      } else if (speaking && remote < SPEAKING_OFF) {
        speaking = false;
        setTurn("listening");
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => cancelAnimationFrame(raf);
  }, [inCall, localLevel, remoteLevel]);

  return turn;
}

/**
 * SIP-specific instance of the reusable VoiceAssistantScreen: maps sip.js's call lifecycle
 * onto the orb's idle/listening/thinking/speaking/error states and wires mute/end-call to the
 * live call. `sip` is the object returned by useSipAudioClient(config); `onClose` unmounts the
 * screen (called once the call is fully torn down). Shared between the landing page demo call
 * and the flow editor's "Web Call" test console.
 */
export function CallOrb({sip, onClose, extension, title = "OpenBrain Voice Agent"}) {
  // sip.mediaTick isn't read directly, but it's part of the `sip` object returned by
  // useSipAudioClient — every state change there (including a stream swap) gives us a new
  // `sip` reference and re-renders this component, so getStreams() below always sees current data.
  const streams = sip.getStreams();
  const inCall = sip.status === "in-call";
  const turn = useInCallTurn(inCall, streams.local, streams.remote);

  // Transient text layered over STATUS_TEXT for lifecycle edges (see the constants above):
  // answered → ready-to-talk right as a call connects, and call-ended / connection-lost when
  // it drops. Tracked against the *previous* status so these fire once, on the transition,
  // not on every render while a status is held.
  const [phaseText, setPhaseText] = useState(null);
  const prevStatusRef = useRef(sip.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = sip.status;
    if (prev === sip.status) return undefined;

    if (CONNECTED_ISH_STATUSES.has(prev) && sip.status === "error") {
      console.info(`${LOG} connection to PBX lost`);
      setPhaseText(CONNECTION_LOST_TEXT);
      return undefined;
    }

    if (prev === "in-call" && sip.status !== "in-call") {
      console.info(`${LOG} call ended`);
      setPhaseText(CALL_ENDED_TEXT);
      return undefined;
    }

    if (sip.status === "in-call" && prev !== "in-call") {
      console.info(`${LOG} voice assistant answered`);
      setPhaseText(ANSWERED_TEXT);
      const toReady = setTimeout(() => setPhaseText(READY_TEXT), 1200);
      const toClear = setTimeout(() => setPhaseText(null), 4000);
      return () => {
        clearTimeout(toReady);
        clearTimeout(toClear);
      };
    }

    setPhaseText(null);
    return undefined;
  }, [sip.status]);

  // "Call ended" reads better on the idle-style orb than the spinning "thinking" look — the
  // call is over, there's nothing left to wait on.
  const state =
    phaseText === CALL_ENDED_TEXT
      ? "idle"
      : inCall
      ? turn
      : sip.status === "error"
      ? "error"
      : sip.status === "idle" || sip.status === "ended"
      ? "idle"
      : "thinking";
  const statusText = phaseText || (inCall ? undefined : STATUS_TEXT[sip.status]);

  // The screen opens straight into placing the call — this is that call, wired as `onStart` so
  // the Ready orb can also retry it by hand (VoiceAssistantScreen only passes `onStart` through
  // while state === "idle", so tapping the live orb mid-call is a no-op, not a second call).
  const handleStartCall = async () => {
    console.info(`${LOG} placing call…`);
    try {
      await sip.call(extension);
    } catch (err) {
      console.error(`${LOG} sip.call() failed:`, err);
      // sip.error already carries the message; VoiceAssistantScreen surfaces it via errorMessage.
    }
  };

  // Auto-start: fires once, right as this screen mounts, so opening it is enough to place the
  // call without a second tap. Empty deps + a ref guard (rather than depending on `sip`, which
  // gets a new object identity on every status change per the comment above) is deliberate —
  // this should run exactly once per screen open, not every time call state changes.
  const didAutoStart = useRef(false);
  useEffect(() => {
    if (didAutoStart.current) return;
    didAutoStart.current = true;
    console.info(`${LOG} voice assistant opened — auto-starting call`);
    handleStartCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plain visibility into the call lifecycle — one line per state the UI actually shows,
  // logged as it happens rather than only on request.
  useEffect(() => {
    console.info(`${LOG} sip.status -> ${sip.status}`);
    if (sip.status === "error" && sip.error) console.error(`${LOG} call error:`, sip.error);
  }, [sip.status, sip.error]);

  useEffect(() => {
    if (inCall) console.info(`${LOG} turn -> ${turn}`);
  }, [inCall, turn]);

  const handleEndCall = async () => {
    console.info(`${LOG} ending call`);
    await sip.endEverything();
    onClose();
  };

  // Once the call drops on its own (far end hangs up, connection lost, etc.) rather than via
  // handleEndCall above, close the screen automatically a few seconds after "Call ended" shows
  // so the user isn't left staring at a dead call console.
  useEffect(() => {
    if (phaseText !== CALL_ENDED_TEXT) return undefined;
    const toClose = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(toClose);
  }, [phaseText, onClose]);

  return (
    <VoiceAssistantScreen
      state={state}
      audioStream={streams.local}
      audioElement={sip.remoteAudioRef.current}
      title={title}
      statusText={statusText}
      errorMessage={sip.error}
      muted={sip.isMuted}
      onToggleMute={sip.toggleMute}
      onEndCall={handleEndCall}
      onStart={handleStartCall}
    />
  );
}
